// Active Directory Waste Analysis — identify unused resources for cost savings

import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';

interface ADWasteData {
  summary: {
    totalOUs: number;
    emptyOUs: number;
    totalGroups: number;
    emptyGroups: number;
    totalUsers: number;
    disabledUsers: number;
    staleUsers: number;
    totalComputers: number;
    disabledComputers: number;
    staleComputers: number;
  };
  emptyGroups: { name: string; dn: string; created: string }[];
  disabledUsers: { name: string; dn: string; lastLogon: string; description: string }[];
  staleUsers: { name: string; dn: string; lastLogon: string; daysSinceLogin: number }[];
  staleComputers: { name: string; dn: string; lastLogon: string; os: string; daysSinceLogin: number }[];
  as_of: string;
}

export function ADWastePage() {
  const { data, isLoading, error, refetch } = useQuery<ADWasteData>({
    queryKey: ['ad-waste'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ad-waste');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const summary = data?.summary;

  const chartData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'OUs', active: summary.totalOUs - summary.emptyOUs, waste: summary.emptyOUs },
      { name: 'Groups', active: summary.totalGroups - summary.emptyGroups, waste: summary.emptyGroups },
      { name: 'Users', active: summary.totalUsers - summary.disabledUsers - summary.staleUsers, waste: summary.disabledUsers + summary.staleUsers },
      { name: 'Computers', active: summary.totalComputers - summary.disabledComputers - summary.staleComputers, waste: summary.disabledComputers + summary.staleComputers },
    ];
  }, [summary]);

  // Table controls for disabled users
  const disabledUserColumns: Column<any>[] = useMemo(() => [
    { key: 'name', label: 'Name', accessor: (r: any) => r.name, render: (r: any) => <span style={{ fontWeight: 500 }}>{r.name}</span> },
    { key: 'description', label: 'Description', accessor: (r: any) => r.description },
    { key: 'lastLogon', label: 'Last Logon', accessor: (r: any) => r.lastLogon, render: (r: any) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.lastLogon && r.lastLogon !== 'Never' ? new Date(r.lastLogon).toLocaleDateString() : 'Never'}</span>, dateField: true },
  ], []);
  const disabledUsersCtrl = useTableControls(data?.disabledUsers || [], disabledUserColumns, { storageKey: 'ad-disabled-users' });

  // Table controls for stale users
  const staleUserColumns: Column<any>[] = useMemo(() => [
    { key: 'name', label: 'Name', accessor: (r: any) => r.name, render: (r: any) => <span style={{ fontWeight: 500 }}>{r.name}</span> },
    { key: 'lastLogon', label: 'Last Logon', accessor: (r: any) => r.lastLogon, render: (r: any) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.lastLogon && r.lastLogon !== 'Never' ? new Date(r.lastLogon).toLocaleDateString() : 'Never'}</span>, dateField: true },
    { key: 'daysSinceLogin', label: 'Days Inactive', accessor: (r: any) => r.daysSinceLogin, render: (r: any) => <span style={{ fontFamily: 'var(--mono)', color: 'var(--risk)', fontWeight: 600 }}>{r.daysSinceLogin}</span> },
  ], []);
  const staleUsersCtrl = useTableControls(data?.staleUsers || [], staleUserColumns, { storageKey: 'ad-stale-users' });

  // Table controls for stale computers
  const staleComputerColumns: Column<any>[] = useMemo(() => [
    { key: 'name', label: 'Name', accessor: (r: any) => r.name, render: (r: any) => <span style={{ fontWeight: 500 }}>{r.name}</span> },
    { key: 'os', label: 'OS', accessor: (r: any) => r.os },
    { key: 'lastLogon', label: 'Last Logon', accessor: (r: any) => r.lastLogon, render: (r: any) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.lastLogon && r.lastLogon !== 'Never' ? new Date(r.lastLogon).toLocaleDateString() : 'Never'}</span>, dateField: true },
    { key: 'daysSinceLogin', label: 'Days Inactive', accessor: (r: any) => r.daysSinceLogin, render: (r: any) => <span style={{ fontFamily: 'var(--mono)', color: 'var(--risk)', fontWeight: 600 }}>{r.daysSinceLogin}</span> },
  ], []);
  const staleComputersCtrl = useTableControls(data?.staleComputers || [], staleComputerColumns, { storageKey: 'ad-stale-computers' });

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Active Directory</p>
              <h1 className="pagehead__title">Savings Analysis</h1>
              <div className="pagehead__facts">
                <span className="fact">Identify unused resources for cost savings</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading AD savings data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Active Directory</p>
              <h1 className="pagehead__title">Savings Analysis</h1>
              <div className="pagehead__facts">
                <span className="fact">Identify unused resources for cost savings</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load AD savings data</h3>
                <p className="state__body">
                  Could not connect to the AD savings API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!data) return null;

  const totalWaste = summary
    ? summary.emptyOUs + summary.emptyGroups + summary.disabledUsers + summary.staleUsers + summary.disabledComputers + summary.staleComputers
    : 0;

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Active Directory</p>
            <h1 className="pagehead__title">Savings Analysis</h1>
            <div className="pagehead__facts">
              <span className="fact">Identify unused resources for cost savings</span>
              {data.as_of && (
                <span className="fact">As of <b>{new Date(data.as_of).toLocaleString()}</b></span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Completeness notice */}
        {summary && (
          <div className={`notice ${totalWaste > 0 ? 'notice--warn' : 'notice--ok'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <div>
              <p className="notice__title">AD Savings Summary</p>
              <p className="notice__body">
                Found <strong>{totalWaste.toLocaleString()} savings items</strong> across OUs, groups, users, and computers.
                {summary.emptyOUs > 0 && <> <strong>{summary.emptyOUs}</strong> empty OUs.</>}
                {summary.emptyGroups > 0 && <> <strong>{summary.emptyGroups}</strong> empty groups.</>}
                {summary.disabledUsers + summary.staleUsers > 0 && <> <strong>{summary.disabledUsers + summary.staleUsers}</strong> user accounts at risk.</>}
                {summary.disabledComputers + summary.staleComputers > 0 && <> <strong>{summary.disabledComputers + summary.staleComputers}</strong> computer accounts at risk.</>}
              </p>
            </div>
          </div>
        )}

        {/* Metric indicators */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="AD savings metrics">
            <div className="ind ind--breach">
              <div className="ind__head">
                <p className="ind__abbr">Empty OUs</p>
                <p className="ind__full">Organizational units with no objects</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.emptyOUs.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalOUs > 0 ? ((summary.emptyOUs / summary.totalOUs) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Savings</b> — of {summary.totalOUs.toLocaleString()} total OUs
              </p>
            </div>

            <div className="ind ind--breach">
              <div className="ind__head">
                <p className="ind__abbr">Empty Groups</p>
                <p className="ind__full">Groups with no members</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.emptyGroups.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalGroups > 0 ? ((summary.emptyGroups / summary.totalGroups) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Savings</b> — of {summary.totalGroups.toLocaleString()} total groups
              </p>
            </div>

            <div className="ind ind--watch">
              <div className="ind__head">
                <p className="ind__abbr">Disabled Users</p>
                <p className="ind__full">User accounts with disabled status</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.disabledUsers.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalUsers > 0 ? ((summary.disabledUsers / summary.totalUsers) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i style={{ background: 'var(--gold)' }}></i>
                <b style={{ color: 'var(--gold)' }}>Monitor</b> — of {summary.totalUsers.toLocaleString()} total users
              </p>
            </div>

            <div className="ind ind--breach">
              <div className="ind__head">
                <p className="ind__abbr">Stale Users</p>
                <p className="ind__full">No login in 90+ days</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.staleUsers.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalUsers > 0 ? ((summary.staleUsers / summary.totalUsers) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Savings</b> — of {summary.totalUsers.toLocaleString()} total users
              </p>
            </div>

            <div className="ind ind--watch">
              <div className="ind__head">
                <p className="ind__abbr">Disabled Computers</p>
                <p className="ind__full">Computer accounts with disabled status</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.disabledComputers.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalComputers > 0 ? ((summary.disabledComputers / summary.totalComputers) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i style={{ background: 'var(--gold)' }}></i>
                <b style={{ color: 'var(--gold)' }}>Monitor</b> — of {summary.totalComputers.toLocaleString()} total computers
              </p>
            </div>

            <div className="ind ind--breach">
              <div className="ind__head">
                <p className="ind__abbr">Stale Computers</p>
                <p className="ind__full">No login in 90+ days</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{summary.staleComputers.toLocaleString()}</p>
                <span className="ind__delta ind__delta--bad">
                  {summary.totalComputers > 0 ? ((summary.staleComputers / summary.totalComputers) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <p className="ind__reading">
                <i></i>
                <b>Savings</b> — of {summary.totalComputers.toLocaleString()} total computers
              </p>
            </div>
          </div>
        )}

        {/* Savings breakdown chart */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Breakdown</p>
              <h2 className="box__title">Savings by Category</h2>
            </div>
          </div>
          <div className="box__body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={80} />
                <Tooltip />
                <Legend />
                <Bar dataKey="active" stackId="a" fill="var(--ok, #2E7D5B)" name="Active" radius={[0, 0, 0, 0]} />
                <Bar dataKey="waste" stackId="a" fill="var(--risk, #B4485C)" name="Savings" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Details grid — Empty Groups & Disabled Users */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Groups</p>
                <h2 className="box__title">Empty Groups</h2>
              </div>
              <div className="box__head-actions">
                <span className="pill pill--risk"><i></i> {data.emptyGroups.length}</span>
              </div>
            </div>
            <div className="box__body box__body--flush">
              {data.emptyGroups.length === 0 ? (
                <div className="state">
                  <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h3 className="state__title">No empty groups</h3>
                  <p className="state__body">All groups have at least one member.</p>
                </div>
              ) : (
                <div className="tbl-scroll" style={{ maxHeight: 300 }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th><button className="th">Name</button></th>
                        <th><button className="th">DN</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.emptyGroups.map((g, i) => (
                        <tr key={i}>
                          <td className="td--name">{g.name}</td>
                          <td className="td--id" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{g.dn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Users</p>
                <h2 className="box__title">Disabled Users</h2>
              </div>
              <div className="box__head-actions">
                <span className="pill pill--warn"><i></i> {data.disabledUsers.length}</span>
              </div>
            </div>
            <div className="box__body box__body--flush">
              {data.disabledUsers.length === 0 ? (
                <div className="state">
                  <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h3 className="state__title">No disabled users</h3>
                  <p className="state__body">All user accounts are active.</p>
                </div>
              ) : (
                <div className="tbl-scroll" style={{ maxHeight: 300 }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th><button className="th">Name</button></th>
                        <th><button className="th">DN</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.disabledUsers.map((u, i) => (
                        <tr key={i}>
                          <td className="td--name">{u.name}</td>
                          <td className="td--id" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{u.dn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stale accounts — full DataTable sections */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Disabled Accounts</p>
              <h2 className="box__title">Disabled User Accounts</h2>
            </div>
            <div className="box__head-actions">
              <span className="pill pill--warn"><i></i> {data.disabledUsers.length}</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            <DataTable
              columns={disabledUserColumns}
              visibleColumns={disabledUsersCtrl.visibleColumns}
              data={disabledUsersCtrl.paged}
              search={disabledUsersCtrl.search}
              onSearchChange={disabledUsersCtrl.setSearch}
              sortKey={disabledUsersCtrl.sortKey}
              sortDir={disabledUsersCtrl.sortDir}
              onSort={disabledUsersCtrl.setSort}
              page={disabledUsersCtrl.page}
              totalPages={disabledUsersCtrl.totalPages}
              totalFiltered={disabledUsersCtrl.totalFiltered}
              pageSize={disabledUsersCtrl.pageSize}
              onPageChange={disabledUsersCtrl.setPage}
              onPageSizeChange={disabledUsersCtrl.setPageSize}
              activeFilters={disabledUsersCtrl.activeFilters}
              onFilter={disabledUsersCtrl.setFilter}
              onClearFilters={disabledUsersCtrl.clearFilters}
              timeRange={disabledUsersCtrl.timeRange}
              onTimeRangeChange={disabledUsersCtrl.setTimeRange}
              hiddenColumns={disabledUsersCtrl.hiddenColumns}
              onToggleColumn={disabledUsersCtrl.toggleColumn}
              selectedIds={disabledUsersCtrl.selectedIds}
              onToggleSelect={disabledUsersCtrl.toggleSelect}
              onSelectAll={disabledUsersCtrl.selectAll}
              onClearSelection={disabledUsersCtrl.clearSelection}
              savedViews={disabledUsersCtrl.savedViews}
              onSaveView={disabledUsersCtrl.saveView}
              onLoadView={disabledUsersCtrl.loadView}
              onDeleteView={disabledUsersCtrl.deleteView}
              onExportCSV={disabledUsersCtrl.exportCSV}
              onExportExcel={disabledUsersCtrl.exportExcel}
              keyAccessor={(r) => r.dn || r.name}
              searchPlaceholder="Search disabled users..."
            />
          </div>
        </div>

        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Stale Accounts</p>
              <h2 className="box__title">Stale Users 90+ Days</h2>
            </div>
            <div className="box__head-actions">
              <span className="pill pill--risk"><i></i> {data.staleUsers.length}</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            <DataTable
              columns={staleUserColumns}
              visibleColumns={staleUsersCtrl.visibleColumns}
              data={staleUsersCtrl.paged}
              search={staleUsersCtrl.search}
              onSearchChange={staleUsersCtrl.setSearch}
              sortKey={staleUsersCtrl.sortKey}
              sortDir={staleUsersCtrl.sortDir}
              onSort={staleUsersCtrl.setSort}
              page={staleUsersCtrl.page}
              totalPages={staleUsersCtrl.totalPages}
              totalFiltered={staleUsersCtrl.totalFiltered}
              pageSize={staleUsersCtrl.pageSize}
              onPageChange={staleUsersCtrl.setPage}
              onPageSizeChange={staleUsersCtrl.setPageSize}
              activeFilters={staleUsersCtrl.activeFilters}
              onFilter={staleUsersCtrl.setFilter}
              onClearFilters={staleUsersCtrl.clearFilters}
              timeRange={staleUsersCtrl.timeRange}
              onTimeRangeChange={staleUsersCtrl.setTimeRange}
              hiddenColumns={staleUsersCtrl.hiddenColumns}
              onToggleColumn={staleUsersCtrl.toggleColumn}
              selectedIds={staleUsersCtrl.selectedIds}
              onToggleSelect={staleUsersCtrl.toggleSelect}
              onSelectAll={staleUsersCtrl.selectAll}
              onClearSelection={staleUsersCtrl.clearSelection}
              savedViews={staleUsersCtrl.savedViews}
              onSaveView={staleUsersCtrl.saveView}
              onLoadView={staleUsersCtrl.loadView}
              onDeleteView={staleUsersCtrl.deleteView}
              onExportCSV={staleUsersCtrl.exportCSV}
              onExportExcel={staleUsersCtrl.exportExcel}
              keyAccessor={(r) => r.dn || r.name}
              searchPlaceholder="Search stale users..."
            />
          </div>
        </div>

        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Stale Computers</p>
              <h2 className="box__title">Stale Computers 90+ Days</h2>
            </div>
            <div className="box__head-actions">
              <span className="pill pill--risk"><i></i> {data.staleComputers.length}</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            <DataTable
              columns={staleComputerColumns}
              visibleColumns={staleComputersCtrl.visibleColumns}
              data={staleComputersCtrl.paged}
              search={staleComputersCtrl.search}
              onSearchChange={staleComputersCtrl.setSearch}
              sortKey={staleComputersCtrl.sortKey}
              sortDir={staleComputersCtrl.sortDir}
              onSort={staleComputersCtrl.setSort}
              page={staleComputersCtrl.page}
              totalPages={staleComputersCtrl.totalPages}
              totalFiltered={staleComputersCtrl.totalFiltered}
              pageSize={staleComputersCtrl.pageSize}
              onPageChange={staleComputersCtrl.setPage}
              onPageSizeChange={staleComputersCtrl.setPageSize}
              activeFilters={staleComputersCtrl.activeFilters}
              onFilter={staleComputersCtrl.setFilter}
              onClearFilters={staleComputersCtrl.clearFilters}
              timeRange={staleComputersCtrl.timeRange}
              onTimeRangeChange={staleComputersCtrl.setTimeRange}
              hiddenColumns={staleComputersCtrl.hiddenColumns}
              onToggleColumn={staleComputersCtrl.toggleColumn}
              selectedIds={staleComputersCtrl.selectedIds}
              onToggleSelect={staleComputersCtrl.toggleSelect}
              onSelectAll={staleComputersCtrl.selectAll}
              onClearSelection={staleComputersCtrl.clearSelection}
              savedViews={staleComputersCtrl.savedViews}
              onSaveView={staleComputersCtrl.saveView}
              onLoadView={staleComputersCtrl.loadView}
              onDeleteView={staleComputersCtrl.deleteView}
              onExportCSV={staleComputersCtrl.exportCSV}
              onExportExcel={staleComputersCtrl.exportExcel}
              keyAccessor={(r) => r.dn || r.name}
              searchPlaceholder="Search stale computers..."
            />
          </div>
        </div>
      </div>
    </>
  );
}
